import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { type DefaultRawDatum } from '@nivo/pie';
import type BigNumber from 'bignumber.js';
import classNames from 'classnames';
import { chainflipAssetMap, formatUsdValue, TokenAmount } from '@/shared/utils';
import { FancyDollarDisplay, SkeletonLine } from '..';
import QuestionMarkTooltip from '../QuestionMarkTooltip';

export type DistributionData = DefaultRawDatum & { color: string };

const Chart = ({ data }: { data: DistributionData[] }) => {
  const total = data.reduce((acc, item) => acc + item.value, 0);

  return (
    <div className="flex h-2 w-full rounded-lg">
      {data
        .filter((item) => item.value !== 0)
        .map((item) => (
          <div
            className="border"
            style={{
              width: `${Math.round((item.value / total) * 100)}%`,
              backgroundColor: item.color,
              borderColor: item.color,
            }}
          />
        ))}
    </div>
  );
};

export const ResponsiveDistributionChart = ({
  title,
  tooltipContent,
  totalValueUsd,
  data,
  loading,
  icon,
  isMobileView = false,
  displayTotalValue,
}: {
  title: string;
  tooltipContent: string;
  totalValueUsd: number;
  data: ((
    | { absoluteValue: number }
    | { absoluteValue: BigNumber; asset: ChainflipAsset; absoluteValueSecondaryUsd: number }
  ) & { label: string } & DistributionData)[];
  loading: boolean;
  icon?: JSX.Element;
  isMobileView?: boolean;
  displayTotalValue?: boolean;
}) => (
  <div className="cf-card h-full">
    <div className={classNames('gap-y-6', 'flex h-full flex-col text-cf-light-2')}>
      <div className="flex items-center gap-x-1 font-aeonikMedium text-14">
        {icon}
        {title}
        <QuestionMarkTooltip content={tooltipContent} />
      </div>
      {displayTotalValue && (
        <FancyDollarDisplay
          value={formatUsdValue(totalValueUsd)}
          wholeClassName="text-24"
          decimalClassName="text-14"
        />
      )}
      {totalValueUsd !== 0 && <Chart data={data} />}
      <div
        className={classNames(
          isMobileView && 'row-start-2 !gap-y-2 md:row-start-1',
          'flex w-full flex-col justify-between gap-y-3',
        )}
      >
        <div className="flex flex-col gap-y-4">
          {data.map((item) => (
            <div key={item.id} className="flex justify-between gap-x-3 text-14 text-cf-light-3">
              <div className="flex items-center gap-x-1 font-aeonikMedium">
                <div
                  className="h-2.5 w-[3px] rounded-md"
                  style={{
                    backgroundColor: item.color,
                  }}
                />
                {item.label}
              </div>
              {loading ? (
                <SkeletonLine width={50} />
              ) : (
                <div className="flex gap-x-1">
                  {'asset' in item ? (
                    <>
                      <span className="font-aeonikMedium text-cf-white">
                        {TokenAmount.fromAsset(item.absoluteValue, item.asset).toFormatted()}{' '}
                        {chainflipAssetMap[item.asset].symbol}
                      </span>
                      <span className="font-aeonikMedium text-cf-light-3">
                        ({formatUsdValue(item.absoluteValueSecondaryUsd)})
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="font-aeonikMedium text-cf-white">
                        {formatUsdValue(item.absoluteValue)}
                      </span>
                      <span className="font-aeonikMedium text-cf-light-3">
                        (
                        {`${((totalValueUsd > 0 ? item.absoluteValue / totalValueUsd : 0) * 100).toFixed(2)}%`}
                        )
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);
